import React from "react";
import {
  Box,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  makeStyles,
  Chip,
  Typography,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Clear, FilterList } from "@material-ui/icons";
import { useTranslation } from "../hooks/useTranslation";

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    backgroundColor: "var(--ikaro-bg-surface)",
    borderRadius: 0,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: "1px solid var(--ikaro-border-default)",
  },
  filterSection: {
    marginBottom: theme.spacing(2), // Reduz margem
  },
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  filterField: {
    marginBottom: 0,
    "& .MuiOutlinedInput-root": {
      height: 48,
    },
  },
  clearButton: {
    textTransform: "none",
    height: 40,
    padding: "0 16px",
  },
  activeFiltersContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    borderRadius: 8,
    border: "1px solid var(--ikaro-border-default)",
  },
  activeFiltersTitle: {
    fontSize: "0.875rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  filterChip: {
    margin: theme.spacing(0.5),
  },
  gridContainer: {
    "& .MuiGrid-item": {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
}));

const JobFilters = ({
  filters,
  filterFields,
  onFilterChange,
  onClearFilters,
  gridColumns = 5,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const { t: tCommon } = useTranslation("common");

  const handleFilterChange = (field, value) => {
    onFilterChange(field, value);
  };

  const handleClearFilters = () => {
    onClearFilters();
  };

  const handleRemoveFilter = (fieldKey) => {
    handleFilterChange(fieldKey, "");
  };

  const getActiveFilters = () => {
    return filterFields.filter((field) => {
      const value = filters[field.key];
      if (field.type === "checkbox") return value === true;
      return value && value !== "";
    });
  };

  const getFilterDisplayValue = (field) => {
    const value = filters[field.key];
    if (field.type === "checkbox")
      return value ? tCommon("yes") : tCommon("no");
    if (
      field.type === "autocomplete" &&
      typeof value === "object" &&
      value.name
    ) {
      return value.name;
    }
    if (field.type === "select" && field.options) {
      const option = field.options.find((opt) => opt.value === value);
      return option ? option.label : value;
    }
    return value;
  };

  const renderFilterField = (field) => {
    const value = filters[field.key] || "";

    switch (field.type) {
      case "autocomplete":
        return (
          <Autocomplete
            fullWidth
            options={field.options || []}
            getOptionLabel={
              field.getOptionLabel ||
              ((option) => option.label || option.name || option)
            }
            value={value}
            onChange={(event, newValue) => {
              if (field.onChange) {
                field.onChange(newValue);
              }
              handleFilterChange(field.key, newValue);
            }}
            onInputChange={(event, newInputValue) => {
              if (field.onInputChange) {
                if (!newInputValue && field.onClear) {
                  field.onClear();
                }
                field.onInputChange(newInputValue);
              }
            }}
            loading={field.loading || false}
            disabled={field.disabled || false}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                variant="outlined"
                placeholder={field.placeholder}
                className={classes.filterField}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {field.loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(option) => (
              <Box>
                <Typography variant="body1">
                  {field.getOptionLabel
                    ? field.getOptionLabel(option)
                    : option.label || option.name || option}
                </Typography>
              </Box>
            )}
          />
        );

      case "select":
        return (
          <FormControl
            variant="outlined"
            fullWidth
            className={classes.filterField}
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFilterChange(field.key, e.target.value)}
              label={field.label}
            >
              <MenuItem value="">
                <em>{tCommon("all")}</em>
              </MenuItem>
              {field.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "date":
        return (
          <TextField
            type="date"
            label={field.label}
            variant="outlined"
            fullWidth
            value={value}
            onChange={(e) => handleFilterChange(field.key, e.target.value)}
            className={classes.filterField}
            InputLabelProps={{ shrink: true }}
          />
        );

      case "checkbox":
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) =>
                  handleFilterChange(field.key, e.target.checked)
                }
                color="primary"
              />
            }
            label={field.label}
            className={classes.filterField}
          />
        );

      case "text":
      default:
        return (
          <TextField
            label={field.label}
            variant="outlined"
            fullWidth
            value={value}
            onChange={(e) => handleFilterChange(field.key, e.target.value)}
            placeholder={
              field.placeholder || `Filtrar por ${field.label.toLowerCase()}`
            }
            className={classes.filterField}
          />
        );
    }
  };

  const activeFilters = getActiveFilters();

  return (
    <Paper className={classes.filtersContainer} elevation={1}>
      <div className={classes.filterSection}>
        <Typography className={classes.sectionTitle}>
          <FilterList />
          {t("filters.filtersTitle")}
        </Typography>

        <Grid container spacing={3} className={classes.gridContainer}>
          {filterFields.map((field) => (
            <Grid item xs={12} sm={6} md={12 / gridColumns} key={field.key}>
              {renderFilterField(field)}
            </Grid>
          ))}
        </Grid>

        <div className={classes.buttonContainer}>
          <Button
            variant="outlined"
            className={classes.clearButton}
            startIcon={<Clear />}
            onClick={handleClearFilters}
            disabled={activeFilters.length === 0}
          >
            {t("filters.clearFilters")}
          </Button>
        </div>
      </div>

      {/* Filtros ativos */}
      {activeFilters.length > 0 && (
        <div className={classes.activeFiltersContainer}>
          <Typography className={classes.activeFiltersTitle}>
            {t("filters.activeFilters")} ({activeFilters.length})
          </Typography>
          <Box>
            {activeFilters.map((field) => (
              <Chip
                key={field.key}
                label={`${field.label}: ${getFilterDisplayValue(field)}`}
                onDelete={() => handleRemoveFilter(field.key)}
                className={classes.filterChip}
                size="small"
              />
            ))}
          </Box>
        </div>
      )}
    </Paper>
  );
};

export default JobFilters;
